import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { instalarOrigemBlack } from "@/lib/black2x1/origem-visita";
import { CookieConsent } from "@/components/CookieConsent";
import { usePageTracking } from "@/hooks/use-page-tracking";
import { GTM_CONTAINER_ID } from "@/lib/tracking-config";
import appCss from "../styles.css?url";



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Fisio em Ortopedia" },
      { property: "og:site_name", content: "Fisio em Ortopedia" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "IZT-ISB7GN_yrXyUPImQFma1DaUxqnUR7LVtkk8ZC7U" },
    ],


    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Rastreio (GTM → Meta Pixel): conexões abertas cedo para o PageView disparar antes do visitante sair (connect rate).
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "preconnect", href: "https://connect.facebook.net" },
      { rel: "preconnect", href: "https://server.fisioemortopedia.com.br" },
      // Apenas os pesos realmente usados (300 a 800) — menos bytes de fonte no primeiro paint.
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap" },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    ],
    scripts: [
      {
        children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`,
      },
      // Meta Pixel base desativado no site — Pixel + CAPI agora são disparados pelo GTM
      // usando os eventos feo_lead_saved / feo_lead_qualified do dataLayer.
    ],



  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>

        {/* Meta Pixel noscript removido — rastreio Meta agora é feito via GTM */}

        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEmbed = pathname.startsWith("/embed/");
  const isApi = pathname.startsWith("/api/");
  const isAdmin = pathname.startsWith("/admin");
  const isSiteRoute = !isEmbed && !isApi && !isAdmin;

  return (
    <QueryClientProvider client={queryClient}>
      {!isEmbed && <TrackingMount />}
      {isSiteRoute && <OrganizationJsonLd />}
      <Outlet />
      <Toaster />
      {!isEmbed && <CookieConsent />}
    </QueryClientProvider>
  );
}

function TrackingMount() {
  usePageTracking();
  useEffect(() => instalarOrigemBlack(), []);
  return null;
}

const ORGANIZATION_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "EducationalOrganization",
      "@id": "https://fisioemortopedia.com.br/#organization",
      name: "Fisio em Ortopedia",
      alternateName: ["FEO", "Fisio em Ortopedia (FEO)"],
      url: "https://fisioemortopedia.com.br",
      logo: "https://fisioemortopedia.com.br/favicon.ico",
      description:
        "A FEO é referência nacional em pós-graduação e educação continuada para fisioterapeutas em Ortopedia, Dor, Esporte, Gerontologia, Traumato-ortopedia e Prescrição de Exercícios. Pós-graduações 100% online, 360h, reconhecidas pelo MEC.",
      areaServed: "BR",
      inLanguage: "pt-BR",
      knowsAbout: [
        "Fisioterapia Musculoesquelética",
        "Fisioterapia em Ortopedia",
        "Fisioterapia em Dor",
        "Fisioterapia Esportiva",
        "Fisioterapia em Gerontologia",
        "Fisioterapia em Traumato-ortopedia",
        "Prescrição de Exercícios para Fisioterapeutas",
        "Reabilitação Musculoesquelética",
        "Prática Baseada em Evidências",
      ],
      hasOfferCatalog: [
        {
          "@type": "OfferCatalog",
          name: "STAR e Agente de Raciocínio Clínico",
          itemListElement: [
            { "@type": "Course", name: "STAR Cursos", url: "https://fisioemortopedia.com.br/star" },
            { "@type": "Product", name: "Agente de Raciocínio Clínico Fukusawa", url: "https://fisioemortopedia.com.br/agente-raciocinio" },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Pós-graduações para Fisioterapeutas",
          itemListElement: [
            { "@type": "Course", name: "Pós-graduação em Fisioterapia em Ortopedia", url: "https://pos-orto.fisioemortopedia.com.br/" },
            { "@type": "Course", name: "Pós-graduação em Dor para Fisioterapeutas", url: "https://pos-dor.fisioemortopedia.com.br/" },
            { "@type": "Course", name: "Pós-graduação em Fisioterapia Esportiva", url: "https://pos-esporte.fisioemortopedia.com.br/" },
            { "@type": "Course", name: "Pós-graduação em Fisioterapia em Gerontologia", url: "https://pos-geronto.fisioemortopedia.com.br/" },
            { "@type": "Course", name: "Pós-graduação em Fisioterapia em Traumato-ortopedia", url: "https://pos-traumato.fisioemortopedia.com.br/" },
            { "@type": "Course", name: "Pós-graduação em Prescrição de Exercícios para Fisioterapeutas", url: "https://pos-prescricao.fisioemortopedia.com.br/" },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Certificações de 60h para Fisioterapeutas",
          itemListElement: [
            { "@type": "Course", name: "Certificação em Quadril", url: "https://fisioemortopedia.com.br/certificacao-quadril" },
            { "@type": "Course", name: "Certificação em Coluna Lombar", url: "https://fisioemortopedia.com.br/certificacao-lombar" },
            { "@type": "Course", name: "Certificação em Coluna Cervical", url: "https://fisioemortopedia.com.br/certificacao-cervical" },
            { "@type": "Course", name: "Certificação em Ombro", url: "https://fisioemortopedia.com.br/certificacao-ombro" },
            { "@type": "Course", name: "Certificação em Joelho", url: "https://fisioemortopedia.com.br/certificacao-joelho" },
            { "@type": "Course", name: "Certificação em Tornozelo e Pé", url: "https://fisioemortopedia.com.br/certificacao-tornozelo" },
            { "@type": "Course", name: "Certificação em Agentes Eletrofísicos", url: "https://fisioemortopedia.com.br/certificacao-eletro" },
          ],
        },
      ],
      sameAs: [
        "https://www.instagram.com/fisioemortopedia/",
        "https://www.youtube.com/@fisioemortopedia",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://fisioemortopedia.com.br/#website",
      url: "https://fisioemortopedia.com.br",
      name: "Fisio em Ortopedia",
      inLanguage: "pt-BR",
      publisher: { "@id": "https://fisioemortopedia.com.br/#organization" },
    },
  ],
});

function OrganizationJsonLd() {
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: ORGANIZATION_JSONLD }}
    />
  );
}



